import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Spinner } from "../Bits";
import ImageUpload from "../ImageUpload";
import { BLOCK_TYPES, getBlockMeta } from "../../lib/blocks";
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Save,
  Eye,
  EyeOff,
  Pencil,
  X,
} from "lucide-react";

export default function BlocksAdmin() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/cms/admin/pages");
      setPages(r.data || []);
      setSelectedId((cur) => cur || r.data?.[0]?.id || null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPage = useCallback(async (id) => {
    if (!id) {
      setPage(null);
      return;
    }
    const r = await api.get(`/cms/admin/pages/${id}`);
    setPage(r.data);
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);
  useEffect(() => { loadPage(selectedId); }, [selectedId, loadPage]);

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-blocks">
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        {/* Sidebar: page list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="overline">CMS-Seiten</div>
            <button
              onClick={() => setCreating(true)}
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full"
              style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}
              data-testid="blocks-new-page"
            >
              <Plus className="w-3.5 h-3.5" /> Neue Seite
            </button>
          </div>
          <div className="space-y-2">
            {pages.length === 0 && (
              <div className="text-xs opacity-60 p-3 border rounded-lg" style={{ borderColor: "var(--wp-border)" }}>
                Noch keine Seite. Lege jetzt deine erste an.
              </div>
            )}
            {pages.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className="w-full text-left p-3 rounded-lg border transition-colors"
                style={{
                  borderColor: selectedId === p.id ? "var(--wp-olive)" : "var(--wp-border)",
                  backgroundColor: selectedId === p.id ? "var(--wp-cream)" : "white",
                }}
                data-testid={`blocks-page-${p.slug}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{p.title}</span>
                  <span className="text-[10px] uppercase px-2 py-0.5 rounded-full" style={{
                    backgroundColor: p.status === "published" ? "rgba(90,110,63,0.15)" : "rgba(0,0,0,0.06)",
                    color: p.status === "published" ? "var(--wp-olive)" : "var(--wp-text-muted)",
                  }}>
                    {p.status === "published" ? "Live" : "Entwurf"}
                  </span>
                </div>
                <div className="text-[11px] opacity-60 truncate mt-1">/p/{p.slug}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Main: page editor */}
        <div>
          {creating ? (
            <CreatePageForm
              onCancel={() => setCreating(false)}
              onCreated={(newPage) => {
                setCreating(false);
                loadPages();
                setSelectedId(newPage.id);
              }}
            />
          ) : page ? (
            <PageEditor
              page={page}
              onChanged={() => loadPage(selectedId)}
              onPageListChanged={loadPages}
              onDeleted={() => {
                setSelectedId(null);
                setPage(null);
                loadPages();
              }}
            />
          ) : (
            <div className="plant-card p-12 text-center text-sm opacity-60">
              Wähle eine Seite aus oder erstelle eine neue.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =================== Create Page ===================
function CreatePageForm({ onCancel, onCreated }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/cms/admin/pages", {
        title,
        slug: slug || title,
        status: "draft",
        show_in_nav: false,
      });
      toast.success("Seite angelegt");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="plant-card p-6 max-w-lg" data-testid="blocks-create-form">
      <div className="overline mb-1">Neue Seite</div>
      <h3 className="font-display text-2xl mb-5" style={{ color: "var(--wp-olive)" }}>Seite anlegen</h3>
      <label className="block mb-3">
        <div className="overline mb-1">Titel</div>
        <input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wp-border)" }}
          data-testid="blocks-create-title"
        />
      </label>
      <label className="block mb-5">
        <div className="overline mb-1">Slug (optional)</div>
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="wird automatisch aus dem Titel generiert"
          className="w-full px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wp-border)" }}
          data-testid="blocks-create-slug"
        />
      </label>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy} className="btn-primary" data-testid="blocks-create-submit">
          {busy ? "Lege an …" : "Anlegen"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">Abbrechen</button>
      </div>
    </form>
  );
}

// =================== Page Editor ===================
function PageEditor({ page, onChanged, onPageListChanged, onDeleted }) {
  const [meta, setMeta] = useState({
    title: page.title || "",
    slug: page.slug || "",
    status: page.status || "draft",
    show_in_nav: !!page.show_in_nav,
    nav_label: page.nav_label || "",
    meta_description: page.meta_description || "",
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setMeta({
      title: page.title || "",
      slug: page.slug || "",
      status: page.status || "draft",
      show_in_nav: !!page.show_in_nav,
      nav_label: page.nav_label || "",
      meta_description: page.meta_description || "",
    });
  }, [page.id, page.title, page.slug, page.status, page.show_in_nav, page.nav_label, page.meta_description]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await api.put(`/cms/admin/pages/${page.id}`, meta);
      toast.success("Seite gespeichert");
      onChanged();
      onPageListChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingMeta(false);
    }
  };

  const deletePage = async () => {
    if (!window.confirm(`Seite "${page.title}" wirklich löschen?`)) return;
    try {
      await api.delete(`/cms/admin/pages/${page.id}`);
      toast.success("Seite gelöscht");
      onDeleted();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const addBlock = async (type) => {
    const meta_def = getBlockMeta(type);
    try {
      await api.post(`/cms/admin/pages/${page.id}/blocks`, { type, data: meta_def.defaults });
      setAdding(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Block konnte nicht hinzugefügt werden");
    }
  };

  const reorder = async (blockId, dir) => {
    const sorted = [...(page.blocks || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex((b) => b.id === blockId);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= sorted.length) return;
    const newOrder = [...sorted];
    const [removed] = newOrder.splice(idx, 1);
    newOrder.splice(target, 0, removed);
    try {
      await api.put(`/cms/admin/pages/${page.id}/reorder`, { order: newOrder.map((b) => b.id) });
      onChanged();
    } catch {
      toast.error("Reihenfolge konnte nicht geändert werden");
    }
  };

  const deleteBlock = async (blockId) => {
    if (!window.confirm("Block löschen?")) return;
    try {
      await api.delete(`/cms/admin/pages/${page.id}/blocks/${blockId}`);
      onChanged();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const updateBlock = async (blockId, data) => {
    try {
      await api.put(`/cms/admin/pages/${page.id}/blocks/${blockId}`, { data });
      toast.success("Block gespeichert");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const sortedBlocks = [...(page.blocks || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-5">
      {/* Meta section */}
      <div className="plant-card p-6">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="overline">Seiten-Einstellungen</div>
            <h3 className="font-display text-2xl" style={{ color: "var(--wp-olive)" }}>{page.title}</h3>
            <div className="text-xs opacity-60 mt-1 flex items-center gap-2">
              URL:
              <a
                href={`/p/${page.slug}`}
                target="_blank"
                rel="noreferrer"
                className="underline inline-flex items-center gap-1"
                style={{ color: "var(--wp-olive)" }}
                data-testid="blocks-open-page"
              >
                /p/{page.slug} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveMeta} disabled={savingMeta} className="btn-primary !py-2 !px-4" data-testid="blocks-save-meta">
              <Save className="w-4 h-4" /> {savingMeta ? "Speichere …" : "Speichern"}
            </button>
            <button onClick={deletePage} className="btn-secondary !py-2 !px-4" style={{ color: "var(--wp-danger, #b94a48)" }} data-testid="blocks-delete-page">
              <Trash2 className="w-4 h-4" /> Löschen
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label>
            <div className="overline mb-1">Titel</div>
            <input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                   className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="blocks-meta-title" />
          </label>
          <label>
            <div className="overline mb-1">Slug</div>
            <input value={meta.slug} onChange={(e) => setMeta({ ...meta, slug: e.target.value })}
                   className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="blocks-meta-slug" />
          </label>
          <label>
            <div className="overline mb-1">Status</div>
            <select value={meta.status} onChange={(e) => setMeta({ ...meta, status: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="blocks-meta-status">
              <option value="draft">Entwurf (nicht öffentlich)</option>
              <option value="published">Veröffentlicht (live)</option>
            </select>
          </label>
          <label className="flex items-center gap-2 mt-7">
            <input type="checkbox" checked={meta.show_in_nav} onChange={(e) => setMeta({ ...meta, show_in_nav: e.target.checked })}
                   data-testid="blocks-meta-nav" />
            <span className="text-sm">Im Hauptmenü anzeigen</span>
          </label>
          {meta.show_in_nav && (
            <label>
              <div className="overline mb-1">Menü-Label (optional)</div>
              <input value={meta.nav_label} onChange={(e) => setMeta({ ...meta, nav_label: e.target.value })}
                     placeholder={meta.title}
                     className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="blocks-meta-nav-label" />
            </label>
          )}
          <label className="md:col-span-2">
            <div className="overline mb-1">Meta-Beschreibung (SEO)</div>
            <textarea value={meta.meta_description} onChange={(e) => setMeta({ ...meta, meta_description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="blocks-meta-description" />
          </label>
        </div>
      </div>

      {/* Blocks list */}
      <div className="plant-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Inhalt</div>
            <h4 className="font-display text-xl" style={{ color: "var(--wp-olive)" }}>Blöcke ({sortedBlocks.length})</h4>
          </div>
          <button onClick={() => setAdding(true)} className="btn-primary !py-2 !px-4" data-testid="blocks-add">
            <Plus className="w-4 h-4" /> Block hinzufügen
          </button>
        </div>

        {adding && (
          <div className="mb-5 p-4 rounded-xl border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Block-Typ wählen</div>
              <button onClick={() => setAdding(false)} className="text-xs opacity-60 hover:opacity-100" data-testid="blocks-add-cancel">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {BLOCK_TYPES.map((bt) => (
                <button
                  key={bt.type}
                  onClick={() => addBlock(bt.type)}
                  className="text-left p-3 rounded-lg border transition-colors hover:bg-white"
                  style={{ borderColor: "var(--wp-border)" }}
                  data-testid={`blocks-add-type-${bt.type}`}
                >
                  <i className={`fa-solid ${bt.icon} mb-1.5`} style={{ color: "var(--wp-olive)" }} />
                  <div className="text-sm font-medium">{bt.label}</div>
                  <div className="text-[11px] opacity-60 mt-0.5 line-clamp-2">{bt.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {sortedBlocks.length === 0 ? (
          <div className="text-sm opacity-60 py-8 text-center border-2 border-dashed rounded-xl" style={{ borderColor: "var(--wp-border)" }}>
            Noch keine Blöcke. Füge oben den ersten Block hinzu.
          </div>
        ) : (
          <div className="space-y-3">
            {sortedBlocks.map((b, idx) => (
              <BlockRow
                key={b.id}
                block={b}
                isFirst={idx === 0}
                isLast={idx === sortedBlocks.length - 1}
                onMoveUp={() => reorder(b.id, -1)}
                onMoveDown={() => reorder(b.id, 1)}
                onDelete={() => deleteBlock(b.id)}
                onSave={(data) => updateBlock(b.id, data)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =================== Block Row (collapsed + expanded form) ===================
function BlockRow({ block, isFirst, isLast, onMoveUp, onMoveDown, onDelete, onSave }) {
  const meta = getBlockMeta(block.type);
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(block.data || {});

  useEffect(() => { setData(block.data || {}); }, [block.id, block.data]);

  return (
    <div className="rounded-xl border" style={{ borderColor: "var(--wp-border)" }} data-testid={`block-row-${block.type}`}>
      <div className="flex items-center gap-3 p-3">
        <div className="flex flex-col">
          <button disabled={isFirst} onClick={onMoveUp} className="opacity-60 hover:opacity-100 disabled:opacity-20" data-testid={`block-up-${block.id}`}>
            <ChevronUp className="w-4 h-4" />
          </button>
          <button disabled={isLast} onClick={onMoveDown} className="opacity-60 hover:opacity-100 disabled:opacity-20" data-testid={`block-down-${block.id}`}>
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
        <i className={`fa-solid ${meta.icon} text-base`} style={{ color: "var(--wp-olive)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{meta.label}</div>
          <div className="text-[11px] opacity-60 truncate">{previewSummary(block)}</div>
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="btn-secondary !py-1.5 !px-3 !text-xs" data-testid={`block-edit-${block.id}`}>
          {expanded ? <EyeOff className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
          {expanded ? "Schließen" : "Bearbeiten"}
        </button>
        <button onClick={onDelete} className="text-xs px-2 py-1.5 rounded-full" style={{ color: "var(--wp-danger, #b94a48)" }} data-testid={`block-delete-${block.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {expanded && (
        <div className="p-4 border-t" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }}>
          <BlockForm type={block.type} data={data} onChange={setData} />
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={() => { setData(block.data || {}); setExpanded(false); }}
              className="btn-secondary !py-1.5 !px-3 !text-xs"
            >
              Abbrechen
            </button>
            <button
              onClick={() => { onSave(data); setExpanded(false); }}
              className="btn-primary !py-1.5 !px-3 !text-xs"
              data-testid={`block-save-${block.id}`}
            >
              <Save className="w-3.5 h-3.5" /> Speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function previewSummary(block) {
  const d = block.data || {};
  switch (block.type) {
    case "hero":
    case "heading":
    case "cta":
    case "plant_carousel":
    case "tour_list":
    case "newsletter":
      return d.title || "(ohne Titel)";
    case "text":
      return (d.body || "").slice(0, 80);
    case "image":
      return d.caption || d.alt || d.url || "(leeres Bild)";
    case "gallery":
      return `${(d.images || []).length} Bilder`;
    case "video":
      return d.url || "(keine URL)";
    case "spacer":
      return `Abstand: ${d.size || "md"}`;
    default:
      return "";
  }
}

// =================== Block Forms (dynamic per type) ===================
function FieldInput({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
        style={{ borderColor: "var(--wp-border)" }}
        data-testid={testid}
      />
    </label>
  );
}

function FieldArea({ label, value, onChange, rows = 5, placeholder, testid }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
        style={{ borderColor: "var(--wp-border)" }}
        data-testid={testid}
      />
    </label>
  );
}

function FieldImage({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <ImageUpload value={value} onChange={onChange} endpoint="/admin/uploads" testId={testid} />
      <input
        placeholder="oder Bild-URL"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border text-xs mt-2 bg-white"
        style={{ borderColor: "var(--wp-border)" }}
      />
      {value && (
        <div className="mt-2 rounded-lg overflow-hidden border" style={{ borderColor: "var(--wp-border)" }}>
          <img src={value} alt="" className="w-full h-32 object-cover" />
        </div>
      )}
    </div>
  );
}

function BlockForm({ type, data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });

  if (type === "hero") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldImage label="Hintergrundbild" value={data.image} onChange={(v) => set("image", v)} testid="hero-image" />
        <div className="space-y-3">
          <FieldInput label="Overline" value={data.overline} onChange={(v) => set("overline", v)} testid="hero-overline" />
          <FieldInput label="Titel" value={data.title} onChange={(v) => set("title", v)} testid="hero-title" />
          <FieldArea label="Untertitel" value={data.subtitle} onChange={(v) => set("subtitle", v)} rows={3} testid="hero-subtitle" />
          <div className="grid grid-cols-2 gap-2">
            <FieldInput label="CTA-Text" value={data.cta_text} onChange={(v) => set("cta_text", v)} testid="hero-cta-text" />
            <FieldInput label="CTA-Link" value={data.cta_link} onChange={(v) => set("cta_link", v)} placeholder="/tours" testid="hero-cta-link" />
          </div>
        </div>
      </div>
    );
  }
  if (type === "heading") {
    return (
      <div className="space-y-3">
        <FieldInput label="Overline" value={data.overline} onChange={(v) => set("overline", v)} testid="heading-overline" />
        <FieldInput label="Titel" value={data.title} onChange={(v) => set("title", v)} testid="heading-title" />
        <FieldArea label="Untertitel" value={data.subtitle} onChange={(v) => set("subtitle", v)} rows={3} testid="heading-subtitle" />
      </div>
    );
  }
  if (type === "text") {
    return (
      <FieldArea
        label="Text (Markdown-light: **fett**, *kursiv*, [Link](url))"
        value={data.body}
        onChange={(v) => set("body", v)}
        rows={10}
        testid="text-body"
      />
    );
  }
  if (type === "image") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldImage label="Bild" value={data.url} onChange={(v) => set("url", v)} testid="image-url" />
        <div className="space-y-3">
          <FieldInput label="Alt-Text (Barrierefreiheit)" value={data.alt} onChange={(v) => set("alt", v)} testid="image-alt" />
          <FieldArea label="Bildunterschrift" value={data.caption} onChange={(v) => set("caption", v)} rows={3} testid="image-caption" />
        </div>
      </div>
    );
  }
  if (type === "gallery") {
    const images = data.images || [];
    return (
      <div>
        <div className="overline mb-2">Bilder ({images.length})</div>
        <div className="space-y-3">
          {images.map((img, i) => {
            const url = typeof img === "string" ? img : img.url || "";
            const caption = typeof img === "string" ? "" : img.caption || "";
            return (
              <div key={i} className="flex items-start gap-3 p-3 border rounded-lg" style={{ borderColor: "var(--wp-border)" }}>
                <img src={url} alt="" className="w-20 h-20 object-cover rounded" />
                <div className="flex-1 space-y-2">
                  <input
                    value={url}
                    onChange={(e) => {
                      const copy = [...images];
                      copy[i] = { ...(typeof copy[i] === "string" ? { url: copy[i] } : copy[i]), url: e.target.value };
                      set("images", copy);
                    }}
                    placeholder="Bild-URL"
                    className="w-full px-3 py-2 rounded border text-sm bg-white" style={{ borderColor: "var(--wp-border)" }}
                  />
                  <input
                    value={caption}
                    onChange={(e) => {
                      const copy = [...images];
                      copy[i] = { ...(typeof copy[i] === "string" ? { url: copy[i] } : copy[i]), caption: e.target.value };
                      set("images", copy);
                    }}
                    placeholder="Bildunterschrift (optional)"
                    className="w-full px-3 py-2 rounded border text-xs bg-white" style={{ borderColor: "var(--wp-border)" }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => set("images", images.filter((_, j) => j !== i))}
                  className="text-xs opacity-60 hover:opacity-100"
                  style={{ color: "var(--wp-danger, #b94a48)" }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
        <ImageUpload
          value=""
          onChange={(v) => set("images", [...(data.images || []), { url: v, alt: "", caption: "" }])}
          endpoint="/admin/uploads"
          testId="gallery-upload"
        />
        <button
          type="button"
          onClick={() => set("images", [...(data.images || []), { url: "", alt: "", caption: "" }])}
          className="btn-secondary !py-1.5 !px-3 !text-xs mt-3"
        >
          <Plus className="w-3.5 h-3.5" /> Bild manuell
        </button>
      </div>
    );
  }
  if (type === "cta") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <FieldInput label="Titel" value={data.title} onChange={(v) => set("title", v)} testid="cta-title" />
          <FieldArea label="Untertitel" value={data.subtitle} onChange={(v) => set("subtitle", v)} rows={3} testid="cta-subtitle" />
        </div>
        <div className="space-y-3">
          <FieldInput label="Button-Text" value={data.button_text} onChange={(v) => set("button_text", v)} testid="cta-button-text" />
          <FieldInput label="Button-Link" value={data.button_link} onChange={(v) => set("button_link", v)} placeholder="/tours oder https://…" testid="cta-button-link" />
          <label className="block">
            <div className="overline mb-1">Hintergrundfarbe</div>
            <input
              type="color"
              value={data.bg_color || "#5a6e3f"}
              onChange={(e) => set("bg_color", e.target.value)}
              className="h-10 w-20 rounded border" style={{ borderColor: "var(--wp-border)" }}
              data-testid="cta-bg-color"
            />
          </label>
          <FieldImage label="Hintergrundbild (optional)" value={data.image} onChange={(v) => set("image", v)} testid="cta-image" />
        </div>
      </div>
    );
  }
  if (type === "plant_carousel") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldInput label="Titel" value={data.title} onChange={(v) => set("title", v)} testid="pc-title" />
        <FieldInput label="Anzahl" type="number" value={data.limit || 6} onChange={(v) => set("limit", v)} testid="pc-limit" />
        <label>
          <div className="overline mb-1">Kategorie</div>
          <select value={data.category || "all"} onChange={(e) => set("category", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="pc-category">
            <option value="all">Alle</option>
            <option value="edible">Essbar</option>
            <option value="medicinal">Heilkraut</option>
            <option value="toxic">Giftig</option>
            <option value="tree">Baum</option>
            <option value="shrub">Strauch</option>
          </select>
        </label>
        <label>
          <div className="overline mb-1">Saison (Monat)</div>
          <select value={data.season || "all"} onChange={(e) => set("season", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="pc-season">
            <option value="all">Alle Monate</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"][m-1]}</option>
            ))}
          </select>
        </label>
      </div>
    );
  }
  if (type === "video") {
    return (
      <div className="space-y-3">
        <FieldInput label="Video-URL (YouTube oder Vimeo)" value={data.url} onChange={(v) => set("url", v)} placeholder="https://youtube.com/watch?v=…" testid="video-url" />
        <FieldInput label="Bildunterschrift" value={data.caption} onChange={(v) => set("caption", v)} testid="video-caption" />
      </div>
    );
  }
  if (type === "tour_list") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldInput label="Titel" value={data.title} onChange={(v) => set("title", v)} testid="tl-title" />
        <FieldInput label="Anzahl" type="number" value={data.limit || 3} onChange={(v) => set("limit", v)} testid="tl-limit" />
      </div>
    );
  }
  if (type === "newsletter") {
    return (
      <div className="space-y-3">
        <FieldInput label="Titel" value={data.title} onChange={(v) => set("title", v)} testid="nl-title" />
        <FieldArea label="Untertitel" value={data.subtitle} onChange={(v) => set("subtitle", v)} rows={2} testid="nl-subtitle" />
      </div>
    );
  }
  if (type === "spacer") {
    return (
      <label className="block max-w-xs">
        <div className="overline mb-1">Größe</div>
        <select value={data.size || "md"} onChange={(e) => set("size", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="spacer-size">
          <option value="sm">Klein</option>
          <option value="md">Mittel</option>
          <option value="lg">Groß</option>
        </select>
      </label>
    );
  }
  return <div className="text-xs opacity-60">Keine Einstellungen für diesen Block-Typ.</div>;
}
